import { Suspense } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import PublicHostRouter from '@/pages/PublicHostRouter';
import PublicQuizPage from '@/pages/PublicQuiz';
import { PublicPreloader } from '@/components/public/PublicPreloader';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { isPreviewLinkHost } from '@/config/publicEntryHosts';
import { PUBLIC_APEX, PUBLIC_PREVIEW_SUBDOMAIN } from '@/config/public-host';
import { SHOOT_PAGE_PATH } from '@/player/shootPagePath';

/**
 * WEB-1988 — маршруты ВТОРОГО входа сборки (`public.html` + `public-main.tsx`),
 * который раздаётся на клиентских доменах и на поддомене рендера.
 *
 * Перехватывающий маршрут здесь такой же, как в `customDomainRouter`
 * кабинетного роутера: на пользовательском домене адрес страницы это не путь
 * нашего приложения, а путь воронки, и разбирает его сервер
 * (`GET /public/resolve` внутри `PublicHostRouter`).
 *
 * WEB-2187 — ВОЗВРАТ ПОКУПАТЕЛЯ ПОСЛЕ ОПЛАТЫ (`/handoff/*`) ТОЖЕ ЗДЕСЬ.
 * Раньше три эти страницы жили в КАБИНЕТНОМ роутере, а раздатчик отдавал им
 * кабинетную оболочку изъятием `not path /handoff/*`. Значит на
 * `/handoff/success` — адрес, на который платёжная система уводит НАСТОЯЩЕГО
 * покупателя сразу после оплаты, и где на экране его почта, имя и код
 * доступа — приезжала кабинетная сборка с кабинетной наблюдаемостью, то есть
 * с ВКЛЮЧЁННОЙ ЗАПИСЬЮ ЭКРАНА (session-replay, WEB-469). WEB-2178 сняла
 * запись экрана с публичного входа воронки; это тот же класс, только адрес
 * другой. Страницы переписаны без Ant Design и переименованы в `Public*` —
 * теперь их покрывают те же правила сторожа публичного входа
 * (`build-guards/publicEntryGuard.ts`), что и остальные страницы покупателя.
 *
 * Чего здесь НЕТ и почему:
 *  - страниц кабинета и конструктора — ради этого разреза карточка WEB-1988 и
 *    заведена (сторож `src/build-guards/publicEntryGuard.ts`);
 *  - ленивой загрузки страницы воронки: первый экран покупателя — это и есть
 *    воронка, и лишний круг «загрузили вход → загрузили страницу» стоит ему
 *    времени до контента. Всё, что не нужно до первого экрана (виджет Stripe,
 *    телеметрия, страницы возврата после оплаты), лениво по отдельности —
 *    поэтому три маршрута ниже именно ленивые: до оплаты они не нужны ни
 *    одним байтом, и в бюджет первого экрана (150 КБ gzip) не входят.
 */
const PublicHandoffSuccessPage = lazyWithRetry(
  () => import('@/pages/PublicHandoffSuccess'),
  'page:PublicHandoffSuccess',
);
const PublicHandoffCancelPage = lazyWithRetry(
  () => import('@/pages/PublicHandoffCancel'),
  'page:PublicHandoffCancel',
);
const PublicHandoffMagicLinkPage = lazyWithRetry(
  () => import('@/pages/PublicHandoffMagicLink'),
  'page:PublicHandoffMagicLink',
);

// WEB-2115: страница получателя ссылки предпросмотра — тоже ЛЕНИВО: обычной
// воронке она не нужна ни байтом, а запаса веса у первого экрана нет.
const PublicPreviewPage = lazyWithRetry(
  () => import('@/pages/PublicPreview'),
  'page:PublicPreview',
);

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PublicPreloader />}>{children}</Suspense>;
}

/**
 * WEB-2115 — перехватывающий маршрут: на поддомене ссылки предпросмотра
 * (`preview.<апекс>/<токен>`) путь — это токен ссылки, а не адрес воронки, и
 * резолв домена его бы отверг. Хост за жизнь страницы не меняется.
 */
function PublicCatchAll() {
  if (
    isPreviewLinkHost(
      window.location.hostname,
      PUBLIC_APEX,
      PUBLIC_PREVIEW_SUBDOMAIN,
    )
  ) {
    return (
      <SuspenseWrap>
        <PublicPreviewPage QuizPage={PublicQuizPage} />
      </SuspenseWrap>
    );
  }
  return <PublicHostRouter />;
}

export const publicRoutes: RouteObject[] = [
  // Страница «оплата прошла» — адрес `success_url` чекаута Stripe.
  {
    path: '/handoff/success',
    element: (
      <SuspenseWrap>
        <PublicHandoffSuccessPage />
      </SuspenseWrap>
    ),
  },
  // WEB-356: `cancel_url` по умолчанию — покупатель, передумавший в чекауте,
  // не должен получить голый «Not found».
  {
    path: '/handoff/cancel',
    element: (
      <SuspenseWrap>
        <PublicHandoffCancelPage />
      </SuspenseWrap>
    ),
  },
  // WEB-708: web-fallback волшебной ссылки из письма `/handoff/<code>` —
  // когда приложение не установлено и перехватить ссылку некому.
  {
    path: '/handoff/:code',
    element: (
      <SuspenseWrap>
        <PublicHandoffMagicLinkPage />
      </SuspenseWrap>
    ),
  },
  // WEB-3913: съёмка нашей воронки службой разбора — страница рисует только
  // тело, которое служба подставила ей по адресу того же источника. Вся
  // страница — ленивым маршрутом (`lazy`): в первом экране обычной воронки от
  // съёмки только эта строка (храповики веса WEB-1988, WEB-2015). Повтор
  // загрузки куска (lazyWithRetry) не нужен: страницу открывает только
  // браузер службы, сбой куска — пустой снимок и повтор задачи службой.
  { path: SHOOT_PAGE_PATH, lazy: () => import('@/pages/PublicShoot') },
  {
    path: '*',
    element: <PublicCatchAll />,
  },
];

export const publicRouter = createBrowserRouter(publicRoutes);
